import {ElementRef} from '@angular/core';
import * as _ from 'lodash-es';
import {ElementStylesPatch} from '../../../../../common/element-styles-patch';
import {ElementRulerDirective} from '../../../../element-ruler';
import {TableColumnsSizingStep} from '../../table';
import {ColumnMeta} from './column-meta';
import {Px} from './table-column-width-units';

interface ColumnsFitContentSizesByClassesDict {
    [tableSizeDetectionClasses: string]: number[];
}

interface SizeGroupMeta {
    sizeGroupId: any;
    columnsMeta: ColumnMeta[];
    fitContentWidthByClassesDict: {[tableSizeDetectionClasses: string]: number};
    widths: number[];
    sumWidth: number;
    previousSumWidth: number;
    /**
     * Set for groups covered by at least one sizing step in the current pass.
     * Groups without it keep their current widths and width units untouched.
     */
    isProcessed: boolean;
}

export class ColumnsAutoResizer {

    public columnsMeta: ColumnMeta[];
    public complexTableElementRef: ElementRef;
    public tableBodyRulerDirective: ElementRulerDirective;
    public columnsSizingSteps: TableColumnsSizingStep[];

    private readonly warnedUnprocessedGroups = new Set<any>();

    public applyAutoResizing(): void {
        const [tableWidth] = this.tableBodyRulerDirective.getSize();

        // Hidden (display:none) or detached table measures as 0. Sizing now
        // would write zeros into widths and (via Pr.adjustToPx ratios)
        // poison persisted column configs.
        if (!(tableWidth > 0)) {
            return;
        }

        const sizeGroupsMeta: SizeGroupMeta[] = this.createSizeGroupsMeta();

        const sizeGroupMetaDict = _.keyBy<SizeGroupMeta>(
            sizeGroupsMeta,
            sizeGroupMeta => sizeGroupMeta.sizeGroupId
        );

        for (const sizingStep of this.columnsSizingSteps) {
            const sizeGroupMeta = sizeGroupMetaDict[sizingStep.sizeGroupId];

            // Skip steps for unknown columns (e.g. hidden columns).
            if (!sizeGroupMeta) {
                continue;
            }

            const restWidth = Math.max(
                tableWidth - this.getSizeGroupsWidthSum(sizeGroupsMeta) + sizeGroupMeta.sumWidth,
                0
            );

            if (restWidth <= 0) {
                // No free space left: stop the waterfall. Groups below keep
                // their current widths instead of collapsing to 0px.
                break;
            }

            sizeGroupMeta.isProcessed = true;

            const groupColumnCount = sizeGroupMeta.columnsMeta.length;

            const groupRequestedWidth = sizingStep.getGroupSize({
                fitContentWidth: sizeGroupMeta.fitContentWidthByClassesDict[sizingStep.tableSizeDetectionClasses || ''],
                restWidth,
                groupColumnCount,
                previousGroupWidth: sizeGroupMeta.previousSumWidth
            });

            // sumWidth is always 0 at this point (group metas are created anew
            // on every pass), so the old lower bound was dead code.
            const groupSumWidth = _.clamp(groupRequestedWidth, 0, restWidth);

            sizeGroupMeta.sumWidth = groupSumWidth;

            const minGroupColumnWidth = (groupSumWidth - groupSumWidth % groupColumnCount) / groupColumnCount;

            sizeGroupMeta.widths = sizeGroupMeta.widths.map((prevWidth, iColumnMeta) =>
                iColumnMeta < groupColumnCount - 1
                    ? minGroupColumnWidth
                    : minGroupColumnWidth + groupSumWidth % groupColumnCount
            );
        }

        sizeGroupsMeta.forEach(groupMeta => {
            // Groups not covered by any step (columns without a sizeGroupId
            // while steps reference only named groups, or groups cut off by
            // the space limit) keep their current widths — previously
            // they were overwritten with 0.
            if (!groupMeta.isProcessed) {
                if (!this.warnedUnprocessedGroups.has(groupMeta.sizeGroupId)) {
                    this.warnedUnprocessedGroups.add(groupMeta.sizeGroupId);
                    console.warn(
                        `ColumnsAutoResizer: size group "${String(groupMeta.sizeGroupId)}" is not covered ` +
                        `by any executed sizing step (missing in columnsSizingSteps or cut off by the ` +
                        `space limit); current column widths are kept.`
                    );
                }
                return;
            }

            groupMeta.columnsMeta.forEach((columnMeta, iColumnMeta) => {
                const widthPx = groupMeta.widths[iColumnMeta];

                columnMeta.width = new Px(widthPx);
                columnMeta.widthPx = widthPx;
            });
        });
    }

    private getSizeGroupsWidthSum(sizeGroupsMeta: SizeGroupMeta[]): number {
        return sizeGroupsMeta.reduce(
            (sumWidth, currentSizeGroupMeta) => sumWidth + currentSizeGroupMeta.sumWidth,
            0
        );
    }

    private detectColumnsFitContentSizes(): ColumnsFitContentSizesByClassesDict {
        const pointerEventsElement: HTMLElement = this.complexTableElementRef.nativeElement;
        const tableElement = (this.tableBodyRulerDirective.elementRef.nativeElement as HTMLElement)
            .querySelector<HTMLTableElement>('table');

        const pointerEventsElementStylesPatch = new ElementStylesPatch(pointerEventsElement, {
            width: '0'
        });
        const tableElementStylesPatch = new ElementStylesPatch(tableElement, {
            tableLayout: 'auto'
        });

        // Null placeholders (see createSizeGroupsMeta) must be skipped.
        const columnsMeta = this.columnsMeta.filter(Boolean);

        columnsMeta.forEach(
            columnMeta =>
                (columnMeta.elements as HTMLTableCellElement[]).forEach(
                    cellElement => cellElement.style.width = null
                )
        );

        const columnsFitContentSizesDict: ColumnsFitContentSizesByClassesDict = this.columnsSizingSteps.reduce(
            (dict, sizingStep) => {
                const sizeDetectionClasses: string = sizingStep.tableSizeDetectionClasses || '';

                if (!dict[sizeDetectionClasses]) {
                    if (sizeDetectionClasses) {
                        tableElement.classList.add(sizeDetectionClasses);
                    }

                    dict[sizeDetectionClasses] = columnsMeta.map(columnMeta => {
                        const columnCellElement: HTMLElement = columnMeta.elements[1] || columnMeta.elements[0];

                        // A column may have no rendered cells yet (e.g. empty
                        // table): contribute 0 instead of crashing.
                        return columnCellElement
                            ? columnCellElement.getBoundingClientRect().width
                            : 0;
                    });

                    if (sizeDetectionClasses) {
                        tableElement.classList.remove(sizeDetectionClasses);
                    }
                }

                return dict;
            },
            {} as ColumnsFitContentSizesByClassesDict
        );

        pointerEventsElementStylesPatch.revert();
        tableElementStylesPatch.revert();

        return columnsFitContentSizesDict;
    }

    private createSizeGroupsMeta(): SizeGroupMeta[] {
        const columnsFitContentSizesDict = this.detectColumnsFitContentSizes();

        // onColumnAdded() inserts a null placeholder until the first cell of
        // the column is rendered (possible for an empty table with no rows):
        // grouping over them would crash on columnDef access.
        const columnsMeta = this.columnsMeta.filter(Boolean);

        return _.uniq(
            columnsMeta.map(columnMeta => columnMeta.columnDef.sizeGroupId)
        )
            .map((sizeGroupId: any): SizeGroupMeta => {
                const groupColumnsMetaRef = columnsMeta.reduce(
                    (ref, columnMeta, iColumnMeta) => {
                        if (columnMeta.columnDef.sizeGroupId === sizeGroupId) {
                            ref.columnsMeta.push(columnMeta);
                            ref.columnMetaIndexes.push(iColumnMeta);
                        }

                        return ref;
                    },
                    {
                        columnsMeta:       [],
                        columnMetaIndexes: []
                    } as {
                        columnsMeta: ColumnMeta[],
                        columnMetaIndexes: number[]
                    }
                );

                return {
                    sizeGroupId,
                    columnsMeta:                  groupColumnsMetaRef.columnsMeta,
                    fitContentWidthByClassesDict: _.mapValues(columnsFitContentSizesDict, fitContentWidths => {
                        const groupFitContentWidths = _.at(fitContentWidths, groupColumnsMetaRef.columnMetaIndexes);

                        return Math.max(...groupFitContentWidths);
                    }),
                    widths:                       new Array(groupColumnsMetaRef.columnsMeta.length).fill(0),
                    sumWidth:                     0,
                    // null widthPx (no unit parsed) contributes 0. NOTE: the old
                    // `sum + columnMeta.widthPx || 0` had an operator-precedence
                    // bug ((sum + widthPx) || 0) — latent, but a trap.
                    previousSumWidth:             groupColumnsMetaRef.columnsMeta
                                                      .reduce((sum, columnMeta) => sum + (columnMeta.widthPx || 0), 0),
                    isProcessed:                  false
                };
            });
    }

}
